import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CrewEntryResponse } from '../models/response/crew-entry.response';
import { DateTime } from 'luxon';
import {
  CrewHistoryItem,
  CrewHistoryResponse,
} from '../models/response/crew-history.response';
import { CrewHistoryService } from '../crew-history.service';
import { Jumpseater } from '@flight-details/standby-list/models/jumpseater';
import { RequestState } from '@shared/models/request-state';
import { Observable, tap } from 'rxjs';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { PilotNameResponse } from '../models/response/pilot-name.response';

@Component({
  selector: 'app-crew-history',
  templateUrl: './crew-history.component.html',
  styleUrl: './crew-history.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrewHistoryComponent implements OnInit {
  empNum?: string;
  name?: string;
  history$?: Observable<RequestState<CrewHistoryResponse>>;
  nameLookup$?: Observable<RequestState<PilotNameResponse>>;
  searchEmpNum = '';

  searchForm = new FormBuilder().group({
    empNum: ['', [Validators.minLength(6)]],
  });

  constructor(
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      pilot?: CrewEntryResponse;
      jumpseater?: Jumpseater;
    },
    private crewHistoryService: CrewHistoryService,
  ) {}

  ngOnInit(): void {
    // Pilot and jumpseater objects have different formats.
    // Map the required fields as necessary.

    if (this.data.pilot) {
      const { pilot } = this.data;
      this.empNum = pilot.employeeNumber;
      this.name = `${pilot.nickname || pilot.firstName} ${pilot.name}`;
      this.history$ = pilot.history$;
    }

    if (this.data.jumpseater) {
      const { jumpseater } = this.data;
      this.empNum = jumpseater.empNumber;
      this.name = `${jumpseater.name}`;
      this.history$ = jumpseater.history$;
    }
  }

  formatDate(date: string): string {
    return DateTime.fromISO(date, { zone: 'UTC' }).toFormat('MM/dd/yyyy');
  }

  /** Counts the total number of legs flown together. */
  getLegCount(historyItems: CrewHistoryItem[] | undefined): number {
    if (!historyItems) {
      return 0;
    }

    let legs = 0;

    for (const item of historyItems) {
      for (const dp of item.dutyPeriodSummaries) {
        legs += dp.legCount;
      }
    }

    return legs;
  }

  onPageClick(page: number): void {
    if (this.empNum) {
      this.history$ = this.crewHistoryService.getFlightsFlownWith(
        this.empNum,
        page,
      );
    }
  }

  numeric(control: AbstractControl): void {
    control.setValue((control.value as string).replace(/\D/, ''));
  }

  onSearchClick(): void {
    this.empNum = this.searchForm.controls.empNum.value || '';

    if (this.empNum && this.empNum.length === 6) {
      this.history$ = this.crewHistoryService.getFlightsFlownWith(this.empNum);
      this.nameLookup$ = this.crewHistoryService.getPilotName(this.empNum).pipe(
        tap((reqState) => {
          if (
            reqState.response &&
            reqState.response.list &&
            reqState.response.list.length > 0
          ) {
            const pilotName = reqState.response.list[0];
            this.name = `${pilotName.nickName || pilotName.firstName} ${
              pilotName.lastName
            }`;
          }
        }),
      );
    }
  }
}
